import React, {
	FunctionComponent,
	ReactNode,
	createContext,
	useContext,
	useState,
} from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogProps,
	DialogTitle,
	IconButton,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

interface DialogOptions {
	title: string;
	content?: JSX.Element;
	hasConfirm?: boolean;
}

interface PromiseInfo {
	resolve: (value: boolean | PromiseLike<boolean>) => void;
	reject: (reason?: any) => void;
}

export type ShowDialogHandler = (options: DialogOptions) => Promise<boolean>;

export type CloseDialogHandler = () => void;

export interface DialogContextType {
	showDialog: ShowDialogHandler;
	closeDialog: CloseDialogHandler;
}

const DialogContext = createContext<DialogContextType>({
	showDialog: () => {
		throw new Error("Component is not wrapped with a DialogProvider.");
	},
	closeDialog: () => {},
});

const DialogProvider: FunctionComponent<{ children: ReactNode }> = ({
	children,
}) => {
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<DialogOptions>({
		title: "",
	});
	const [promiseInfo, setPromiseInfo] = useState<PromiseInfo>();

	const { t } = useResource();

	const showDialog: ShowDialogHandler = (options) => {
		return new Promise<boolean>((resolve, reject) => {
			setPromiseInfo({ resolve, reject });
			setOptions(options);
			setOpen(true);
		});
	};

	const closeDialog: CloseDialogHandler = () => {
		setOpen(false);
		promiseInfo?.resolve(false);
		setPromiseInfo(undefined);
	};

	const handleConfirm = () => {
		setOpen(false);
		promiseInfo?.resolve(true);
		setPromiseInfo(undefined);
	};

	const handleCloseDialog: DialogProps["onClose"] = () => {
		closeDialog();
	};

	const handleCloseButton = () => {
		closeDialog();
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleCloseDialog}
				maxWidth={false}
				fullWidth={true}
				PaperProps={{
					sx: {
						minHeight: "93vh",
						maxHeight: "93vh",
					},
				}}
			>
				<DialogTitle>
					<Typography variant="h1">{options.title}</Typography>
				</DialogTitle>
				<IconButton
					aria-label="close"
					onClick={handleCloseButton}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent sx={{ minWidth: "400px", overflowY: "scroll" }}>
					{!!options.content && <Box>{options.content}</Box>}
				</DialogContent>
				{options.hasConfirm && (
					<DialogActions>
						<Button onClick={handleCloseButton}>
							{t(Resources.Common.Confirm)}
						</Button>
						<Button variant="contained" onClick={handleConfirm}>
							{}
						</Button>
					</DialogActions>
				)}
			</Dialog>
			<DialogContext.Provider value={{ showDialog, closeDialog }}>
				{children}
			</DialogContext.Provider>
		</>
	);
};

export const useDialog = (): DialogContextType => {
	return useContext(DialogContext);
};

export default DialogProvider;
